import { ALL_SERVICES, effectiveService, isServiceOffered } from '@dawn/feature-config/widgets';
import {
  FilterDropdown,
  illustrations,
  WidgetCardShell,
  WidgetCollectingState,
  WidgetErrorState,
  WidgetLoadingState,
} from '@dawn/ui';
import type { ReactNode } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { WidgetConfig } from '@/config/dashboard.types';
import { WIDGET_FILTERS } from '@/config/filters.config';
import { useWidgetDataQuery } from '@/hooks/queries/use-widget-data-query';
import { useConnectedServicesStore } from '@/stores/connected-services';
import { useDashboardFilterStore } from '@/stores/dashboard-filter';
import { useWidgetFilterStore } from '@/stores/widget-filter';

const WIDGET_ILLUSTRATIONS: Record<string, string> = {
  sensitivity: illustrations.folderWithDocument,
  'stale-duplicate-data': illustrations.databaseStackMagnifier,
  'non-human-identities': illustrations.nonHumanIdentityOutline,
};

interface WidgetShellProps {
  config: WidgetConfig;
  /**
   * Called on the `status === 'data'` branch with the envelope's `data` payload.
   * Absent → placeholder stub (widget not yet implemented).
   * Returns null/undefined → `WidgetErrorState` with retry, since `status === 'data'`
   * implies the payload should be renderable; a null/undefined return means the
   * envelope was malformed or failed shape narrowing.
   */
  renderData?: (envelopeData: unknown) => ReactNode;
  /**
   * Optional override for the WidgetCardShell's CardContent padding/spacing.
   * Use sparingly — most widgets accept the default `p-5 pt-6`. Provided as
   * an opt-in escape hatch for widgets whose body's natural rhythm leaves
   * extra whitespace under the default padding.
   */
  contentClassName?: string;
}

// Header-icon ids → Figma-sourced outline SVGs in @dawn/ui. Each SVG carries
// its own natural dimensions and uses `currentColor`, so the wrapper handles
// only sizing/centering. Matches Figma nodes 5:17873 (file), 5:22943 (HI),
// 5:22985 (NHI).
const WIDGET_ICON_SOURCES: Record<string, string> = {
  file: illustrations.fileOutline,
  user: illustrations.humanIdentityOutline,
  bot: illustrations.nonHumanIdentityOutline,
};

function renderWidgetIcon(iconId: string | undefined): ReactNode {
  if (!iconId) return null;
  const src = WIDGET_ICON_SOURCES[iconId];
  if (!src) return null;
  return (
    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center text-dawn-black">
      <img src={src} alt="" aria-hidden="true" />
    </span>
  );
}

export function WidgetShell({ config, renderData, contentClassName }: WidgetShellProps) {
  const globalFilters = useDashboardFilterStore(
    useShallow((state) => {
      const subscribed: Record<string, string> = {};
      for (const id of config.filters?.global ?? []) {
        if (state.filters[id]) subscribed[id] = state.filters[id];
      }
      return subscribed;
    })
  );

  const storedWidgetFilters = useWidgetFilterStore(
    useShallow((state) => state.filters[config.id] ?? {})
  );
  const connected = useConnectedServicesStore((state) => state.services);
  // A saved service may read as all once the list arrives; asking before it would be thrown away.
  const servicesSettled = useConnectedServicesStore((state) => state.settled);
  const stored = storedWidgetFilters.service;
  const ready = servicesSettled || stored === undefined || stored === ALL_SERVICES;
  const widgetFilters =
    storedWidgetFilters.service === undefined
      ? storedWidgetFilters
      : {
          ...storedWidgetFilters,
          service: effectiveService(storedWidgetFilters.service, connected),
        };

  const setWidgetFilter = useWidgetFilterStore((state) => state.setWidgetFilter);

  const { data, isLoading, isError, refetch } = useWidgetDataQuery(
    config,
    globalFilters,
    widgetFilters,
    ready
  );

  const hasDataSource = !!config.dataSource;
  const isMetric = config.variant === 'metric';
  const illustration = WIDGET_ILLUSTRATIONS[config.type];

  const widgetFilterConfigs = (config.filters?.widget ?? [])
    .map((filterId) => WIDGET_FILTERS[filterId])
    .filter((f): f is NonNullable<typeof f> => !!f);

  const headerSlot =
    widgetFilterConfigs.length > 0 ? (
      <div className="flex items-center gap-2">
        {widgetFilterConfigs.map((filterConfig) => (
          <FilterDropdown
            key={filterConfig.id}
            selectionMode="radio"
            triggerLabel={filterConfig.label}
            // An option the tenant cannot use (a service it never connected) is not offered.
            options={filterConfig.options.filter((option) =>
              isServiceOffered(option.value, connected)
            )}
            value={widgetFilters[filterConfig.id] ?? filterConfig.defaultValue ?? ''}
            onChange={(value) =>
              setWidgetFilter(
                config.id,
                filterConfig.id,
                typeof value === 'string' ? value : (value[0] ?? '')
              )
            }
          />
        ))}
      </div>
    ) : null;

  return (
    <WidgetCardShell
      title={config.title}
      icon={renderWidgetIcon(config.icon)}
      headerSlot={headerSlot}
      gridColumn={config.position.column}
      gridColumnSpan={config.position.colSpan ?? 1}
      gridRow={config.position.row}
      gridRowSpan={config.position.rowSpan ?? 1}
      minHeight={config.size?.minHeight}
      contentClassName={contentClassName}
    >
      {renderContent()}
    </WidgetCardShell>
  );

  function renderContent() {
    if (!hasDataSource) {
      return <div className="h-32 rounded bg-grey-200" />;
    }

    // A query held back (settings not hydrated, services not settled) has no data and is not
    // fetching yet; it is still loading as far as the card is concerned.
    if (isLoading || (data === undefined && !isError)) {
      return <WidgetLoadingState />;
    }

    if (isError) {
      return <WidgetErrorState compact={isMetric} onRetry={() => refetch()} />;
    }

    if (data?.status === 'collecting') {
      return (
        <WidgetCollectingState
          title={config.title}
          illustration={illustration}
          compact={isMetric}
        />
      );
    }

    if (data?.status === 'data' && renderData) {
      const rendered = renderData(data.data);
      if (rendered !== null && rendered !== undefined) {
        return rendered;
      }
      return <WidgetErrorState compact={isMetric} onRetry={() => refetch()} />;
    }

    return (
      <div className="flex h-32 items-center justify-center rounded bg-grey-100">
        <p className="text-[13px] leading-[1.3] text-grey-400">
          {config.title} — data ready (placeholder)
        </p>
      </div>
    );
  }
}
